import { useEffect, useState } from "react";

import clsx from "clsx";
import { useDebounce } from "@uidotdev/usehooks";
import { CheckIcon } from "@heroicons/react/24/outline";

import Modal, { ModalControlProps } from "../../layouts/Modal";
import { Button } from "../base/Button";
import { Toggle } from "../base/Toggle";
import { TextField } from "../base/TextField";
import LoadingWheel from "../shared/LoadingWheel";
import useUserSearch from "../../data/User/useUserSearch";
import { IUserSearchResult } from "../../models/User/User";
import useCustomerUsersMutations from "../../data/CustomerUser/useCustomerUsersMutations";

interface AddExistingUserModalProps extends ModalControlProps {
  customerId: string;
}

const ROLE_TOGGLES: { key: string; label: string }[] = [
  { key: "isAdmin", label: "Admin" },
  { key: "isStaff", label: "Staff" },
  { key: "isDriver", label: "Driver" },
  { key: "isMechanic", label: "Mechanic" },
  { key: "isExporter", label: "Exporter" },
];

const emptyRoles = () =>
  ROLE_TOGGLES.reduce<Record<string, boolean>>((acc, r) => {
    acc[r.key] = false;
    return acc;
  }, {});

/**
 * Adds a user who already has a Wilma login (for another customer) to this customer, with the role
 * they should hold here. They keep their existing username and password.
 */
export function AddExistingUserModal({
  open,
  setOpen,
  customerId,
}: AddExistingUserModalProps) {
  const [rawSearch, setRawSearch] = useState("");
  const searchTerm = useDebounce(rawSearch, 300);
  const [selected, setSelected] = useState<IUserSearchResult>();
  const [roles, setRoles] = useState<Record<string, boolean>>(emptyRoles);
  const [makeDefault, setMakeDefault] = useState(false);

  const { results } = useUserSearch(searchTerm);
  const { addMembership } = useCustomerUsersMutations(customerId);

  useEffect(() => {
    if (!open) {
      setRawSearch("");
      setSelected(undefined);
      setRoles(emptyRoles());
      setMakeDefault(false);
    }
  }, [open]);

  const alreadyMember = (user: IUserSearchResult) =>
    user.memberships.some((m) => m.customerId === customerId);

  const submit = () => {
    if (!selected) return;

    const claims = Object.keys(roles).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = roles[key] ? "true" : "false";
        return acc;
      },
      {},
    );

    addMembership.mutate(
      { userId: selected.id, claims, makeDefault },
      { onSuccess: () => setOpen(false) },
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="!w-1/3">
      <div>
        <div className="border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-1 text-gray-900">
            Add Existing User
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Give someone who already has a Wilma login access to this customer.
            They keep their username and password and can switch between
            customers when they log in.
          </p>
        </div>

        <div className="pt-4 space-y-4">
          <TextField
            label="Search by name, username or email"
            placeholder="Start typing..."
            fullWidth
            value={rawSearch}
            onChange={(e) => {
              setRawSearch(e.currentTarget.value);
              setSelected(undefined);
            }}
          />

          {!selected && searchTerm.trim().length >= 2 && (
            <div className="max-h-56 overflow-y-auto rounded border divide-y">
              {results.isLoading ? (
                <div className="py-3">
                  <LoadingWheel />
                </div>
              ) : results.isError ? (
                <p className="p-3 text-sm text-red-600">
                  Could not search users
                </p>
              ) : results.data && results.data.length > 0 ? (
                results.data.map((u) => {
                  const member = alreadyMember(u);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      disabled={member}
                      onClick={() => setSelected(u)}
                      className={clsx(
                        "w-full text-left px-3 py-2 text-sm",
                        member
                          ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "hover:bg-orange-50",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {u.forename} {u.surname}
                        </span>
                        {member && (
                          <span className="text-xs">Already a member</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {u.username}
                        {u.email && u.email !== u.username && ` · ${u.email}`}
                      </div>
                      {u.memberships.length > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          Member of:{" "}
                          {u.memberships
                            .map((m) => m.customerName ?? m.customerId)
                            .join(", ")}
                        </div>
                      )}
                    </button>
                  );
                })
              ) : (
                <p className="p-3 text-sm text-gray-500">No users found</p>
              )}
            </div>
          )}

          {selected && (
            <>
              <div className="rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm flex items-start gap-2">
                <CheckIcon className="h-5 w-5 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    {selected.forename} {selected.surname}
                  </p>
                  <p className="text-xs text-gray-600">{selected.username}</p>
                  {selected.memberships.length > 0 && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Currently a member of{" "}
                      {selected.memberships
                        .map((m) => m.customerName ?? m.customerId)
                        .join(", ")}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium opacity-80 mb-2">
                  Role at this customer
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {ROLE_TOGGLES.map((r) => (
                    <Toggle
                      key={r.key}
                      label={r.label}
                      checked={roles[r.key]}
                      onChange={(v) => setRoles((prev) => ({ ...prev, [r.key]: v }))}
                    />
                  ))}
                </div>
              </div>

              <Toggle
                label="Make this their default customer"
                checked={makeDefault}
                onChange={setMakeDefault}
              />
              <p className="text-xs text-gray-500 -mt-2">
                The default is used when a login cannot tell which customer the
                user wants.
              </p>

              <div className="flex gap-3 pt-2">
                <Button onClick={submit} disabled={addMembership.isPending}>
                  {addMembership.isPending ? "Adding..." : "Add to customer"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setSelected(undefined)}
                >
                  Choose another
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
